import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Search, Upload, Cloud, FolderOpen, ArrowLeft, File, Film, RefreshCw, Youtube, Eye, ThumbsUp, Clock, Loader2, ChevronDown } from 'lucide-react'
import { api, youtubeApi } from '../api/client'

interface ImportVideoProps {
  onImport: (videoData: {
    url: string
    title: string
    duration: number
    thumbnail?: string
    platform?: string
    cloudSource?: CloudFile
  }) => void
  isLoading: boolean
}

interface CloudConnection {
  provider: string
  email: string
  connected: boolean
}

interface CloudFile {
  id: string
  name: string
  size: number
  mime_type?: string
  modified?: string
  provider: string
  is_folder?: boolean
}

interface CloudFolder {
  id: string
  name: string
  parent_id?: string
}

interface YouTubeVideo {
  video_id: string
  title: string
  description: string
  thumbnail: string
  duration: number
  duration_formatted: string
  published_at: string
  view_count: number
  like_count: number
  comment_count: number
  url: string
}

type ImportTab = 'youtube' | 'upload' | 'url' | 'cloud'

export function ImportVideo({ onImport, isLoading }: ImportVideoProps) {
  const [url, setUrl] = useState('')
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState<ImportTab>('youtube')
  const [connections, setConnections] = useState<CloudConnection[]>([])
  const [loadingConnections, setLoadingConnections] = useState(false)
  const [availableProviders, setAvailableProviders] = useState<Set<string>>(new Set())
  const [loadingProviders, setLoadingProviders] = useState(false)
  
  // File upload state
  const [dragActive, setDragActive] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  // Cloud browser state
  const [browsingProvider, setBrowsingProvider] = useState<string | null>(null)
  const [cloudFiles, setCloudFiles] = useState<CloudFile[]>([])
  const [cloudFolders, setCloudFolders] = useState<CloudFolder[]>([])
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null)
  const [folderStack, setFolderStack] = useState<Array<{ id: string | null; name: string }>>([])
  const [loadingFiles, setLoadingFiles] = useState(false)

  // YouTube channel state
  const [ytConnected, setYtConnected] = useState<boolean | null>(null)
  const [ytEmail, setYtEmail] = useState('')
  const [ytVideos, setYtVideos] = useState<YouTubeVideo[]>([])
  const [ytLoading, setYtLoading] = useState(false)
  const [ytSearchQuery, setYtSearchQuery] = useState('')
  const [ytSortOrder, setYtSortOrder] = useState<'date' | 'viewCount' | 'title'>('date')
  const [ytNextPage, setYtNextPage] = useState<string | null>(null)
  const [ytTotalResults, setYtTotalResults] = useState(0)
  const [ytLoadingMore, setYtLoadingMore] = useState(false)

  // Fetch connected cloud providers on mount
  useEffect(() => {
    fetchProvidersAndConnections()
    checkYouTubeConnection()
  }, [])

  // ── YouTube Channel ──────────────────────────────────────────────
  const checkYouTubeConnection = async () => {
    try {
      const status = await youtubeApi.getStatus()
      setYtConnected(status.connected)
      setYtEmail(status.email || '')
      if (status.connected) {
        loadYtVideos()
      }
    } catch {
      setYtConnected(false)
    }
  }

  const loadYtVideos = async (pageToken?: string, append = false) => {
    if (append) {
      setYtLoadingMore(true)
    } else {
      setYtLoading(true)
      if (!append) setYtVideos([])
    }
    setError('')
    try {
      const data = await youtubeApi.listChannelVideos({
        maxResults: 12,
        pageToken: pageToken || '',
        query: ytSearchQuery,
        order: ytSortOrder,
      })
      if (append) {
        setYtVideos(prev => [...prev, ...data.videos])
      } else {
        setYtVideos(data.videos)
      }
      setYtNextPage(data.next_page_token)
      setYtTotalResults(data.total_results)
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load YouTube videos')
    } finally {
      setYtLoading(false)
      setYtLoadingMore(false)
    }
  }

  const handleYtSearch = (e: React.FormEvent) => {
    e.preventDefault()
    loadYtVideos()
  }

  const handleYtConnect = () => {
    const connectUrl = youtubeApi.getConnectUrl()
    const isInIframe = window !== window.parent
    if (isInIframe) {
      // Open popup for OAuth
      const popup = window.open(connectUrl, '_blank', 'width=600,height=700,menubar=no,toolbar=no')
      
      // Listen for postMessage from callback page
      const messageHandler = (event: MessageEvent) => {
        if (event.data?.type === 'youtube-oauth-complete') {
          window.removeEventListener('message', messageHandler)
          clearInterval(poll)
          if (event.data.success) {
            setYtConnected(true)
            loadYtVideos()
          }
        }
      }
      window.addEventListener('message', messageHandler)
      
      // Also poll as fallback
      const poll = setInterval(async () => {
        try {
          // If popup closed, check one more time then stop
          if (popup?.closed) {
            clearInterval(poll)
            const status = await youtubeApi.getStatus()
            if (status.connected) {
              setYtConnected(true)
              setYtEmail(status.email || '')
              loadYtVideos()
            }
            window.removeEventListener('message', messageHandler)
            return
          }
          const status = await youtubeApi.getStatus()
          if (status.connected) {
            clearInterval(poll)
            window.removeEventListener('message', messageHandler)
            setYtConnected(true)
            setYtEmail(status.email || '')
            loadYtVideos()
            try { popup?.close() } catch {}
          }
        } catch { /* keep polling */ }
      }, 2000)
      setTimeout(() => { clearInterval(poll); window.removeEventListener('message', messageHandler) }, 120000)
    } else {
      window.location.href = connectUrl
    }
  }

  const selectYtVideo = (video: YouTubeVideo) => {
    onImport({
      url: video.url,
      title: video.title,
      duration: video.duration,
      thumbnail: video.thumbnail,
      platform: 'youtube',
    })
  }

  const formatCount = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
    return n.toString()
  }

  const formatDate = (iso: string) => {
    const d = new Date(iso)
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  const fetchProvidersAndConnections = async () => {
    setLoadingProviders(true)
    setLoadingConnections(true)
    try {
      const [providersRes, connectionsRes] = await Promise.allSettled([
        fetch('/api/storage/providers'),
        fetch('/api/storage/connections'),
      ])

      if (providersRes.status === 'fulfilled' && providersRes.value.ok) {
        const data = await providersRes.value.json()
        const keys = Array.isArray(data.providers)
          ? data.providers.map((p: any) => p?.name || p?.key).filter(Boolean)
          : []
        setAvailableProviders(new Set(keys))
      }

      if (connectionsRes.status === 'fulfilled' && connectionsRes.value.ok) {
        const data = await connectionsRes.value.json()
        setConnections(data.connections || [])
      }
    } catch (err) {
      console.error('Failed to fetch providers/connections:', err)
    } finally {
      setLoadingProviders(false)
      setLoadingConnections(false)
    }
  }

  const isProviderConnected = (providerKey: string) => {
    return connections.some(c => c.provider === providerKey && c.connected !== false)
  }

  const getProviderEmail = (providerKey: string) => {
    const conn = connections.find(c => c.provider === providerKey)
    return conn?.email || ''
  }

  const detectPlatform = (url: string) => {
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube'
    if (url.includes('vimeo.com')) return 'vimeo'
    if (url.includes('tiktok.com')) return 'tiktok'
    if (url.includes('instagram.com') || url.includes('instagr.am')) return 'instagram'
    if (url.includes('twitch.tv')) return 'twitch'
    if (url.includes('twitter.com') || url.includes('x.com')) return 'twitter'
    if (url.match(/\.(mp4|mov|avi|mkv|webm)(\?|$)/i)) return 'direct'
    return 'unknown'
  }

  // ── File Upload ──────────────────────────────────────────────────
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true)
    else if (e.type === 'dragleave') setDragActive(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    const files = e.dataTransfer?.files
    if (files?.[0]) handleFileSelected(files[0])
  }, [])

  const handleFileSelected = (file: File) => {
    const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/x-matroska']
    if (!validTypes.includes(file.type) && !file.name.match(/\.(mp4|mov|avi|webm|mkv)$/i)) {
      setError('Please select a video file (MP4, MOV, AVI, WebM, MKV)')
      return
    }
    setError('')
    setUploadFile(file)
  }

  const handleFileUpload = async () => {
    if (!uploadFile) return
    setIsUploading(true)
    setUploadProgress(0)
    setError('')

    try {
      const formData = new FormData()
      formData.append('video', uploadFile)

      const response = await api.post('/studio/import/upload', formData, {
        headers: { 'Content-Type': undefined },  // Let browser set multipart boundary
        timeout: 600000, // 10 min for large files
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100))
        },
      })

      if (response.data?.success) {
        onImport({
          url: response.data.video?.url || response.data.video?.video_path || `upload://${Date.now()}`,
          title: response.data.video?.title || uploadFile.name.replace(/\.[^.]+$/, ''),
          duration: response.data.video?.duration || 0,
        })
      } else {
        setError(response.data?.error || 'Upload failed')
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Upload failed')
    } finally {
      setIsUploading(false)
    }
  }

  // ── URL Import ───────────────────────────────────────────────────
  const handleUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!url.trim()) { setError('Please enter a video URL'); return }

    const platform = detectPlatform(url)
    if (platform === 'unknown') {
      setError('Unsupported URL. Supported: YouTube, Vimeo, TikTok, Instagram, Twitch, X/Twitter, or direct video links.')
      return
    }

    onImport({ url, title: `Video from ${platform}`, duration: 0 })
  }

  // ── Cloud Browser ────────────────────────────────────────────────
  const handleCloudConnect = (providerKey: string, providerName: string) => {
    if (!availableProviders.has(providerKey)) {
      setError(`${providerName} is not configured on this server.`)
      return
    }
    // Open OAuth — use window.open for popup if in iframe, else navigate
    const oauthUrl = `/api/storage/connect/${providerKey}`
    const isInIframe = window !== window.parent
    if (isInIframe) {
      window.open(oauthUrl, '_blank', 'width=600,height=700,menubar=no,toolbar=no')
      // Poll for connection completion
      const pollInterval = setInterval(async () => {
        try {
          const res = await fetch('/api/storage/connections')
          if (res.ok) {
            const data = await res.json()
            const conns: CloudConnection[] = data.connections || []
            setConnections(conns)
            if (conns.some(c => c.provider === providerKey)) {
              clearInterval(pollInterval)
            }
          }
        } catch { /* keep polling */ }
      }, 2000)
      setTimeout(() => clearInterval(pollInterval), 120000) // stop after 2 min
    } else {
      window.location.href = oauthUrl
    }
  }

  const browseCloudProvider = async (providerKey: string, folderId?: string | null) => {
    setBrowsingProvider(providerKey)
    setLoadingFiles(true)
    setError('')
    setCurrentFolderId(folderId || null)

    try {
      const params = new URLSearchParams()
      if (folderId) params.set('folder_id', folderId)

      const [filesRes, foldersRes] = await Promise.allSettled([
        api.get(`/storage/${providerKey}/files?${params}`),
        api.get(`/storage/${providerKey}/folders?${params.toString() ? `parent_id=${folderId || ''}` : ''}`),
      ])

      if (filesRes.status === 'fulfilled') {
        const files: CloudFile[] = (filesRes.value.data?.files || []).map((f: any) => ({
          ...f,
          provider: providerKey,
        }))
        setCloudFiles(files)
      }

      if (foldersRes.status === 'fulfilled') {
        setCloudFolders(foldersRes.value.data?.folders || [])
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load files')
    } finally {
      setLoadingFiles(false)
    }
  }

  const navigateToFolder = (folderId: string, folderName: string) => {
    setFolderStack(prev => [...prev, { id: currentFolderId, name: folderName }])
    browseCloudProvider(browsingProvider!, folderId)
  }

  const navigateBack = () => {
    const prev = [...folderStack]
    const parent = prev.pop()
    setFolderStack(prev)
    browseCloudProvider(browsingProvider!, parent?.id || null)
  }

  const selectCloudFile = (file: CloudFile) => {
    onImport({
      url: `cloud://${file.provider}/${file.id}`,
      title: file.name.replace(/\.[^.]+$/, ''),
      duration: 0,
      cloudSource: file,
    })
  }

  const formatFileSize = (bytes: number) => {
    if (!bytes) return ''
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
  }

  const cloudProviders = [
    { name: 'Google Drive', key: 'google_drive', icon: '📁', accent: 'bg-green-600 hover:bg-green-700' },
    { name: 'Dropbox', key: 'dropbox', icon: '📦', accent: 'bg-blue-500 hover:bg-blue-600' },
    { name: 'OneDrive', key: 'onedrive', icon: '☁️', accent: 'bg-sky-600 hover:bg-sky-700' },
  ]

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-gray-800 rounded-xl border border-gray-600 p-6">
        {/* Header */}
        <div className="text-center mb-5">
          <h2 className="text-2xl font-bold text-white mb-1">Import Video</h2>
          <p className="text-sm text-gray-400">Sign in to YouTube, upload a file, paste a URL, or pick from cloud storage</p>
        </div>

        {/* Tab bar */}
        <div className="flex mb-5 bg-gray-700/60 rounded-lg p-1">
          {([
            { key: 'youtube' as ImportTab, label: '▶️ YouTube', icon: Youtube },
            { key: 'upload' as ImportTab, label: '📤 Upload', icon: Upload },
            { key: 'url' as ImportTab, label: '🔗 URL', icon: Search },
            { key: 'cloud' as ImportTab, label: '☁️ Cloud', icon: Cloud },
          ]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setError('') }}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white hover:bg-gray-600/50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-900/30 border border-red-700/50 text-sm text-red-300">
            {error}
          </div>
        )}

        {/* ── YOUTUBE TAB ────────────────────────────────────────── */}
        {activeTab === 'youtube' && (
          <div className="space-y-4">
            {/* Not connected */}
            {ytConnected === false && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Youtube size={32} className="text-red-500" />
                </div>
                <h3 className="text-white font-semibold mb-2">Sign in to YouTube</h3>
                <p className="text-gray-400 text-sm mb-5 max-w-sm mx-auto">
                  Connect your YouTube channel to browse and import your videos for clipping.
                </p>
                <button
                  onClick={handleYtConnect}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  <Youtube size={20} />
                  Connect YouTube
                </button>
              </div>
            )}

            {/* Checking connection */}
            {ytConnected === null && (
              <div className="text-center py-8">
                <Loader2 size={24} className="text-gray-400 animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-500">Checking YouTube connection...</p>
              </div>
            )}

            {/* Connected — show channel videos */}
            {ytConnected === true && (
              <>
                {/* Connected badge */}
                <div className="flex items-center justify-between bg-gray-700/50 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Youtube size={16} className="text-red-500" />
                    <span className="text-gray-300">Connected as</span>
                    <span className="text-white font-medium">{ytEmail}</span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {ytTotalResults > 0 && `${ytTotalResults} videos`}
                  </span>
                </div>

                {/* Search + Sort */}
                <div className="flex gap-2">
                  <form onSubmit={handleYtSearch} className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={14} />
                    <input
                      type="text"
                      value={ytSearchQuery}
                      onChange={(e) => setYtSearchQuery(e.target.value)}
                      placeholder="Search your videos..."
                      className="w-full pl-9 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                    />
                  </form>
                  <select
                    value={ytSortOrder}
                    onChange={(e) => { setYtSortOrder(e.target.value as any); setTimeout(() => loadYtVideos(), 0) }}
                    className="px-2 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                  >
                    <option value="date">Newest</option>
                    <option value="viewCount">Most Viewed</option>
                    <option value="title">Title A-Z</option>
                  </select>
                </div>

                {/* Video grid */}
                {ytLoading ? (
                  <div className="text-center py-8">
                    <Loader2 size={24} className="text-indigo-400 animate-spin mx-auto mb-2" />
                    <p className="text-sm text-gray-400">Loading your videos...</p>
                  </div>
                ) : ytVideos.length === 0 ? (
                  <div className="text-center py-8">
                    <Youtube size={32} className="text-gray-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">
                      {ytSearchQuery ? 'No videos match that search.' : 'No videos found on your channel.'}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-1">
                      {ytVideos.map((video) => (
                        <button
                          key={video.video_id}
                          onClick={() => selectYtVideo(video)}
                          disabled={isLoading}
                          className="group bg-gray-900 rounded-lg overflow-hidden border border-gray-700/50 hover:border-indigo-500/50 hover:shadow-lg hover:shadow-indigo-500/10 transition-all text-left disabled:opacity-50"
                        >
                          {/* Thumbnail */}
                          <div className="relative aspect-video bg-gray-950">
                            {video.thumbnail ? (
                              <img src={video.thumbnail} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Youtube size={20} className="text-gray-700" />
                              </div>
                            )}
                            <span className="absolute bottom-1 right-1 bg-black/80 text-white text-[10px] px-1 py-0.5 rounded font-mono">
                              {video.duration_formatted}
                            </span>
                            <div className="absolute inset-0 bg-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <span className="bg-indigo-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow-lg">
                                Import →
                              </span>
                            </div>
                          </div>
                          {/* Info */}
                          <div className="p-2">
                            <h4 className="text-xs font-medium text-white line-clamp-2 mb-1 group-hover:text-indigo-300 transition-colors leading-tight">
                              {video.title}
                            </h4>
                            <div className="flex items-center gap-2 text-[10px] text-gray-500">
                              <span className="flex items-center gap-0.5">
                                <Eye size={10} />
                                {formatCount(video.view_count)}
                              </span>
                              <span className="flex items-center gap-0.5">
                                <ThumbsUp size={10} />
                                {formatCount(video.like_count)}
                              </span>
                              <span className="flex items-center gap-0.5">
                                <Clock size={10} />
                                {formatDate(video.published_at)}
                              </span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Load more */}
                    {ytNextPage && (
                      <button
                        onClick={() => loadYtVideos(ytNextPage, true)}
                        disabled={ytLoadingMore}
                        className="w-full py-2 text-sm text-gray-400 hover:text-white flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
                      >
                        {ytLoadingMore ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <ChevronDown size={14} />
                        )}
                        Load more videos
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* ── UPLOAD TAB ─────────────────────────────────────────── */}
        {activeTab === 'upload' && (
          <div className="space-y-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*,.mp4,.mov,.avi,.webm,.mkv"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleFileSelected(e.target.files[0])}
            />

            {!uploadFile ? (
              <div
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${
                  dragActive
                    ? 'border-indigo-500 bg-indigo-600/10'
                    : 'border-gray-600 hover:border-gray-500 hover:bg-gray-700/30'
                }`}
              >
                <Upload size={36} className={`mx-auto mb-3 ${dragActive ? 'text-indigo-400' : 'text-gray-500'}`} />
                <p className="text-sm font-medium text-gray-300 mb-1">
                  {dragActive ? 'Drop your video here' : 'Drag & drop your video here'}
                </p>
                <p className="text-xs text-gray-500">or click to browse — MP4, MOV, AVI, WebM, MKV</p>
              </div>
            ) : (
              <div className="rounded-xl border border-gray-600 bg-gray-700/50 p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Film size={20} className="text-indigo-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{uploadFile.name}</p>
                    <p className="text-xs text-gray-400">{formatFileSize(uploadFile.size)}</p>
                  </div>
                  <button
                    onClick={() => { setUploadFile(null); setUploadProgress(0) }}
                    className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded hover:bg-gray-600"
                  >
                    Change
                  </button>
                </div>

                {isUploading && (
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>Uploading...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="h-1.5 bg-gray-600 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                <button
                  onClick={handleFileUpload}
                  disabled={isUploading || isLoading}
                  className="w-full py-2.5 rounded-lg font-medium text-sm bg-indigo-600 text-white
                    hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isUploading ? 'Uploading...' : 'Upload & Analyze'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── URL TAB ────────────────────────────────────────────── */}
        {activeTab === 'url' && (
          <form onSubmit={handleUrlSubmit} className="space-y-4">
            <div>
              <label htmlFor="url" className="block text-sm font-medium text-gray-300 mb-1.5">
                Video URL
              </label>
              <div className="relative">
                <input
                  type="url"
                  id="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  disabled={isLoading}
                />
                <Search className="absolute right-3 top-3.5 text-gray-500" size={18} />
              </div>
              <p className="mt-1.5 text-xs text-gray-500">
                YouTube, Vimeo, TikTok, Instagram, Twitch, or direct video links
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading || !url.trim()}
              className="w-full py-3 rounded-lg font-medium bg-indigo-600 text-white
                hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Importing...' : 'Import Video'}
            </button>
          </form>
        )}

        {/* ── CLOUD TAB ──────────────────────────────────────────── */}
        {activeTab === 'cloud' && !browsingProvider && (
          <div className="space-y-3">
            {(loadingConnections || loadingProviders) && (
              <p className="text-xs text-gray-500 text-center animate-pulse">Loading accounts...</p>
            )}

            {cloudProviders.map((prov) => {
              const connected = isProviderConnected(prov.key)
              const email = getProviderEmail(prov.key)
              const isAvailable = availableProviders.has(prov.key)
              const isDisabled = loadingProviders || (!isAvailable && !connected)

              return (
                <div
                  key={prov.key}
                  className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                    connected
                      ? 'bg-gray-700/80 border-green-700/40'
                      : 'bg-gray-700/40 border-gray-600/60'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-2xl shrink-0">{prov.icon}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white flex items-center gap-2">
                        {prov.name}
                        {connected && <span className="text-[10px] font-normal px-1.5 py-0.5 rounded-full bg-green-900/60 text-green-400">Connected</span>}
                      </p>
                      {connected && email && (
                        <p className="text-xs text-gray-400 truncate">{email}</p>
                      )}
                      {!connected && !isAvailable && !loadingProviders && (
                        <p className="text-xs text-gray-500">Not configured</p>
                      )}
                    </div>
                  </div>

                  <button
                    disabled={isDisabled}
                    onClick={() =>
                      connected
                        ? browseCloudProvider(prov.key)
                        : handleCloudConnect(prov.key, prov.name)
                    }
                    className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors shrink-0 ${
                      isDisabled
                        ? 'bg-gray-600 cursor-not-allowed opacity-50'
                        : connected
                        ? 'bg-indigo-600 hover:bg-indigo-700'
                        : prov.accent
                    }`}
                  >
                    {connected ? (
                      <span className="flex items-center gap-1.5">
                        <FolderOpen size={14} /> Browse
                      </span>
                    ) : (
                      'Connect'
                    )}
                  </button>
                </div>
              )
            })}

            <button
              onClick={fetchProvidersAndConnections}
              className="w-full mt-2 py-2 text-xs text-gray-500 hover:text-gray-300 flex items-center justify-center gap-1.5 transition-colors"
            >
              <RefreshCw size={12} /> Refresh connections
            </button>
          </div>
        )}

        {/* ── CLOUD FILE BROWSER ─────────────────────────────────── */}
        {activeTab === 'cloud' && browsingProvider && (
          <div className="space-y-3">
            {/* Breadcrumb / Back */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (folderStack.length > 0) {
                    navigateBack()
                  } else {
                    setBrowsingProvider(null)
                    setCloudFiles([])
                    setCloudFolders([])
                    setFolderStack([])
                    setCurrentFolderId(null)
                  }
                }}
                className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft size={16} />
                {folderStack.length > 0 ? 'Back' : 'All providers'}
              </button>
              <span className="text-gray-600">/</span>
              <span className="text-sm text-white font-medium">
                {cloudProviders.find(p => p.key === browsingProvider)?.name || browsingProvider}
              </span>
              {folderStack.map((f, i) => (
                <React.Fragment key={i}>
                  <span className="text-gray-600">/</span>
                  <span className="text-sm text-gray-300">{f.name}</span>
                </React.Fragment>
              ))}
            </div>

            {loadingFiles ? (
              <div className="py-10 text-center">
                <RefreshCw size={20} className="mx-auto text-gray-500 animate-spin mb-2" />
                <p className="text-sm text-gray-400">Loading files...</p>
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto rounded-lg border border-gray-600 divide-y divide-gray-700">
                {/* Folders first */}
                {cloudFolders.map((folder) => (
                  <button
                    key={folder.id}
                    onClick={() => navigateToFolder(folder.id, folder.name)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-700/50 transition-colors text-left"
                  >
                    <FolderOpen size={18} className="text-yellow-500 shrink-0" />
                    <span className="text-sm text-white truncate">{folder.name}</span>
                  </button>
                ))}

                {/* Files */}
                {cloudFiles.map((file) => (
                  <button
                    key={file.id}
                    onClick={() => selectCloudFile(file)}
                    disabled={isLoading}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-indigo-600/10 transition-colors text-left
                      disabled:opacity-50 group"
                  >
                    <Film size={18} className="text-indigo-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate group-hover:text-indigo-300">{file.name}</p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(file.size)}
                        {file.modified && ` · ${new Date(file.modified).toLocaleDateString()}`}
                      </p>
                    </div>
                    <span className="text-xs text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      Select →
                    </span>
                  </button>
                ))}

                {cloudFiles.length === 0 && cloudFolders.length === 0 && (
                  <div className="py-8 text-center text-sm text-gray-500">
                    No video files found in this folder
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Feature bullets */}
        {((activeTab === 'upload' && !uploadFile) || (activeTab === 'youtube' && ytConnected === false)) && (
          <div className="mt-6 pt-5 border-t border-gray-700">
            <div className="grid grid-cols-2 gap-3 text-xs text-gray-400">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
                AI-powered highlight detection
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                Visual timeline editing
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-purple-500 rounded-full" />
                Chat to find clips naturally
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full" />
                One-click export & publish
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}